import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Tab, Tabs, IconButton,
} from '@mui/material';
import { Plus, Trash2, Pencil } from 'lucide-react';

const navy = '#1e3a5f';

const CATEGORIES = [
  { key: 'medic',      label: 'חובשים',       emoji: '🩺' },
  { key: 'paramedic',  label: 'פרמדיקים',     emoji: '🚑' },
  { key: 'maar',       label: 'חוליית חבלה',  emoji: '🛡️' },
];

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

/**
 * EmergencyStep
 * הגדרת מתנדבי חירום לפי קטגוריות: חובשים, פרמדיקים, חוליית חבלה
 *
 * Props:
 *   formData  { emergencyData: { volunteers: { medic, paramedic, maar } } }
 *   onChange  (partial) => void
 */
const EmergencyStep = ({ formData, onChange }) => {
  const volunteers = formData?.emergencyData?.volunteers || { medic: [], paramedic: [], maar: [] };
  const [activeTab, setActiveTab] = useState(0);
  const [draft, setDraft] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const catKey = CATEGORIES[activeTab].key;
  const list = volunteers[catKey] || [];
  const isOpen = draft !== null;

  const updateVolunteers = (newVolunteers) => {
    onChange({ emergencyData: { volunteers: newVolunteers } });
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDraft({ name: '', phone: '' });
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setDraft({ ...list[index] });
  };

  const closeDraft = () => {
    setDraft(null);
    setEditingIndex(null);
  };

  const saveDraft = () => {
    if (!draft.name.trim()) return;
    const entry = { name: draft.name.trim(), phone: draft.phone };
    if (editingIndex !== null) {
      const updated = { ...volunteers, [catKey]: list.map((p, i) => i === editingIndex ? entry : p) };
      updateVolunteers(updated);
    } else {
      const updated = { ...volunteers, [catKey]: [...list, entry] };
      updateVolunteers(updated);
    }
    closeDraft();
  };

  const removePerson = (index) => {
    const updated = { ...volunteers, [catKey]: list.filter((_, i) => i !== index) };
    updateVolunteers(updated);
    if (editingIndex === index) closeDraft();
  };

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    closeDraft();
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        מתנדבי חירום
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
        הוסף את שמות ומספרי הטלפון של מתנדבי החירום לפי קטגוריה.
      </Typography>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, minWidth: 0, px: 2 },
          '& .Mui-selected': { color: navy, fontWeight: 700 },
          '& .MuiTabs-indicator': { bgcolor: navy },
        }}
      >
        {CATEGORIES.map((cat) => (
          <Tab
            key={cat.key}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>{cat.emoji}</span>
                <span>{cat.label}</span>
                {(volunteers[cat.key]?.length > 0) && (
                  <Box sx={{
                    bgcolor: navy, color: 'white', borderRadius: '50%',
                    width: 16, height: 16, fontSize: '0.65rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {volunteers[cat.key].length}
                  </Box>
                )}
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): list */}
        <Box sx={{ flex: isOpen ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button
            startIcon={<Plus size={15} />}
            onClick={openAdd}
            disabled={isOpen}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.8rem', px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}
          >
            הוסף מתנדב
          </Button>

          {list.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {list.map((person, i) => {
                const isActive = editingIndex === i;
                return (
                  <Box key={i} sx={{
                    px: 1.5, py: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: i < list.length - 1 ? '1px solid #f0f0f0' : 'none',
                    borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}>
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827' }}>
                        {person.name}
                      </Typography>
                      {person.phone && (
                        <Typography noWrap sx={{ fontSize: '0.72rem', color: '#6b7280', direction: 'ltr', textAlign: 'right' }}>
                          {person.phone}
                        </Typography>
                      )}
                    </Stack>
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removePerson(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : !isOpen && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              אין מתנדבים בקטגוריה זו
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): form */}
        {isOpen && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box key={editingIndex ?? 'new'} sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="שם"
                  placeholder="שם מלא"
                  value={draft.name}
                  onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                  fullWidth
                  sx={{ ...inputSx, bgcolor: 'white' }}
                />
                <TextField
                  size="small"
                  label="טלפון"
                  placeholder="050-0000000"
                  type="tel"
                  value={draft.phone}
                  onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
                  fullWidth
                  sx={{ ...inputSx, bgcolor: 'white' }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={closeDraft} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
                    ביטול
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveDraft}
                    disabled={!draft.name.trim()}
                    sx={{
                      bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                      boxShadow: 'none', borderRadius: '8px',
                      '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                    }}
                  >
                    {editingIndex !== null ? 'עדכן' : 'הוסף'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmergencyStep;
